import React, { useEffect, useMemo, useState } from 'react';
import { Affix, Button, Card, Col, Form, Input, Row, Select, Space, Tabs } from 'antd';
import { MinusCircleOutlined, PlusOutlined, RobotOutlined } from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useApp } from 'contexts/app';
import { useWorkSpace } from 'contexts/workspace';
import StoreSettingService from 'services/store_setting';
import ImagePicker from 'components/pickers/image/ImagePicker';
import ProductPicker from 'components/pickers/product';

const service = new StoreSettingService();

const normalizeOGImageForForm = (value) => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return { url: value };
  }
  return value;
};

const normalizeOGImageForPayload = (value) => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'object') {
    return value;
  }
  return null;
};

const getImageUrlFromValue = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return String(value).trim();
  }

  if (typeof value === 'object') {
    return String(value?.url || value?.lg_url || value?.md_url || value?.sm_url || value?.src || '').trim();
  }

  return '';
};

const normalizeReferenceImagesForForm = (items = []) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      const url = getImageUrlFromValue(item?.image || item?.url || item?.image_url || item?.src);
      const description = String(item?.description || item?.name || item?.title || item?.alt || '').trim();
      if (!url && !description) {
        return null;
      }

      return {
        image: url ? { url } : null,
        description,
      };
    })
    .filter(Boolean);
};

const normalizeReferenceImagesForPayload = (items = []) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => {
      const url = getImageUrlFromValue(item?.image || item?.url || item?.image_url || item?.src);
      const name = String(item?.description || item?.name || item?.title || item?.alt || '').trim();
      if (!url) {
        return null;
      }

      return {
        url,
        name: name || 'Hình ảnh tham chiếu',
      };
    })
    .filter(Boolean);
};

const LandingEditor = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const { workspace } = useWorkSpace();
  const { showLoading, showNotify, message } = useApp();
  const [isEdit, setIsEdit] = useState(false);

  const pageTitle = useMemo(() => (isEdit ? 'Cập nhật landing page' : 'Tạo landing page mới'), [isEdit]);
  const aiGeneratedLanding = location?.state?.aiGeneratedLanding || null;
  const rawID = String(id || '').trim();
  const normalizedID = rawID && rawID !== '_' && rawID !== 'undefined' && rawID !== 'null' ? rawID : '';

  const loadData = async () => {
    if (!normalizedID) {
      setIsEdit(false);

      const defaultValues = {
        status: 'draft',
        product_ids: [],
        reference_images: [],
        seo: { robots: 'index, follow', keywords: [] },
      };

      const aiValues = aiGeneratedLanding
        ? {
          ...aiGeneratedLanding,
          product_ids: Array.isArray(aiGeneratedLanding?.product_ids) ? aiGeneratedLanding.product_ids : [],
          seo: {
            ...aiGeneratedLanding?.seo,
            keywords: Array.isArray(aiGeneratedLanding?.seo?.keywords) ? aiGeneratedLanding.seo.keywords : [],
            og_image: normalizeOGImageForForm(aiGeneratedLanding?.seo?.og_image),
          },
        }
        : {};

      form.setFieldsValue({
        ...defaultValues,
        ...aiValues,
        seo: {
          ...(defaultValues?.seo || {}),
          ...(aiValues?.seo || {}),
        },
      });
      return;
    }

    setIsEdit(true);
    showLoading(true);
    try {
      const data = await service.getSEOLandingPage({ id: normalizedID });
      form.setFieldsValue({
        ...data,
        product_ids: Array.isArray(data?.product_ids) ? data.product_ids : [],
        reference_images: normalizeReferenceImagesForForm(data?.reference_images),
        seo: {
          ...data?.seo,
          keywords: Array.isArray(data?.seo?.keywords) ? data.seo.keywords : [],
          og_image: normalizeOGImageForForm(data?.seo?.og_image),
        },
      });
    } catch (error) {
      message.error(error?.message || 'Không thể tải dữ liệu landing page');
      navigate('/seo-center/landing');
    }
    showLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [normalizedID, aiGeneratedLanding]);

  const generateSEOMetaByAI = async () => {
    const values = form.getFieldsValue(true);
    if (!String(values?.title || '').trim()) {
      message.error('Vui lòng nhập tiêu đề trước khi tạo meta bằng AI');
      return;
    }

    showLoading(true);
    try {
      const result = await service.generateSEOLandingMetaAI({
        title: values?.title,
        excerpt: values?.excerpt,
        content_html: values?.content_html,
        seo: values?.seo || {},
        product_ids: Array.isArray(values?.product_ids) ? values.product_ids : [],
      });

      form.setFieldsValue({
        title: result?.title || values?.title || '',
        slug: result?.slug || values?.slug || '',
        excerpt: result?.excerpt || values?.excerpt || '',
        seo: {
          ...(values?.seo || {}),
          ...(result?.seo || {}),
        },
      });

      showNotify('Đã tạo meta SEO bằng AI.');
    } catch (error) {
      message.error(error?.message || 'Không thể tạo meta SEO bằng AI');
    }
    showLoading(false);
  };

  const generateLandingHTMLByAI = async () => {
    const values = form.getFieldsValue(true);
    if (!String(values?.title || '').trim()) {
      message.error('Vui lòng nhập tiêu đề trước khi tạo HTML bằng AI');
      return;
    }

    showLoading(true);
    try {
      const result = await service.generateSEOLandingHTMLAI({
        title: values?.title,
        excerpt: values?.excerpt,
        content_html: values?.content_html,
        seo: values?.seo || {},
        product_ids: Array.isArray(values?.product_ids) ? values.product_ids : [],
        reference_images: normalizeReferenceImagesForPayload(values?.reference_images),
      });

      form.setFieldsValue({
        content_html: result?.content_html || '',
      });

      const count = Number(result?.product_count || 0);
      showNotify(count > 0
        ? `Đã tạo HTML bằng AI và thêm block sản phẩm (${count} sản phẩm).`
        : 'Đã tạo HTML bằng AI.');
    } catch (error) {
      message.error(error?.message || 'Không thể tạo HTML landing bằng AI');
    }
    showLoading(false);
  };

  const saveLanding = async (targetStatus = 'draft') => {
    const values = await form.validateFields();
    showLoading(true);
    try {
      const payload = {
        ...values,
        reference_images: normalizeReferenceImagesForPayload(values?.reference_images),
        seo: {
          ...(values?.seo || {}),
          og_image: normalizeOGImageForPayload(values?.seo?.og_image),
        },
        id: normalizedID,
        status: targetStatus,
      };
      const saved = await service.saveSEOLandingPage(payload);
      showNotify(targetStatus === 'published' ? 'Đã publish landing page' : 'Đã lưu nháp landing page');
      if (!normalizedID && saved?.id) {
        navigate(`/seo-center/landing/edit/${saved.id}`);
      }
      await loadData();
    } catch (error) {
      message.error(error?.message || 'Không thể lưu landing page');
    }
    showLoading(false);
  };

  const handleViewLanding = () => {
    const slug = String(form.getFieldValue('slug') || '').trim();
    if (!slug) {
      message.error('Landing page chưa có slug để xem bài viết');
      return;
    }

    const websiteBaseUrl = String(workspace?.websiteUrl || '').replace(/\/$/, '');
    if (!websiteBaseUrl) {
      message.error('Chưa cấu hình website shop để xem bài viết');
      return;
    }

    const landingUrl = `${websiteBaseUrl}/blog/${encodeURIComponent(slug)}`;
    window.open(landingUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', paddingBottom: 88 }}>
      <Card>
        <span style={{ fontWeight: 600 }}>{pageTitle}</span>
      </Card>

      <Form form={form} layout="vertical">
        <Card>
          <Tabs
            items={[
              {
                key: 'meta-seo',
                label: 'Meta SEO',
                children: (
                  <>
                    <Form.Item label="Tiêu đề landing" name="title" rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}>
                      <Input />
                    </Form.Item>

                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item label="Slug" name="slug" rules={[{ required: true, message: 'Vui lòng nhập slug' }]}>
                          <Input placeholder="vd: thuc-pham-si-tp-hcm" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item label="Trạng thái" name="status">
                          <Select options={[{ label: 'Draft', value: 'draft' }, { label: 'Published', value: 'published' }]} />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item label="Tóm tắt" name="excerpt">
                      <Input.TextArea rows={3} />
                    </Form.Item>

                    <Form.Item label="Sản phẩm trong bài viết" name="product_ids">
                      <ProductPicker mode="multiple" placeholder="Chọn sản phẩm để hiển thị dưới bài viết" />
                    </Form.Item>

                    <Form.Item label="Meta title" name={['seo', 'meta_title']}>
                      <Input maxLength={120} />
                    </Form.Item>
                    <Form.Item label="Meta description" name={['seo', 'meta_description']}>
                      <Input.TextArea rows={3} maxLength={320} />
                    </Form.Item>
                    <Form.Item label="Keywords" name={['seo', 'keywords']}>
                      <Select mode="tags" tokenSeparators={[',']} placeholder="Nhập keywords" />
                    </Form.Item>

                    <Form.Item label="OG title" name={['seo', 'og_title']}>
                      <Input />
                    </Form.Item>
                    <Form.Item label="OG description" name={['seo', 'og_description']}>
                      <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item label="OG image" name={['seo', 'og_image']}>
                      <ImagePicker />
                    </Form.Item>

                    <Space style={{ marginTop: 4 }}>
                      <Button onClick={generateSEOMetaByAI} icon={<RobotOutlined />}>
                        Tạo meta SEO bằng AI
                      </Button>
                    </Space>
                  </>
                ),
              },
              {
                key: 'html-tailwind',
                label: 'HTML',
                children: (
                  <>
                    <Form.Item label="Nội dung HTML" name="content_html">
                      <Input.TextArea rows={14} placeholder="Nhập nội dung HTML landing" />
                    </Form.Item>

                    <Form.List name="reference_images">
                      {(fields, { add, remove }) => (
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {fields.map(({ key, name, ...restField }) => (
                            <Row key={key} gutter={12} align="middle">
                              <Col xs={24} md={8}>
                                <Form.Item
                                  {...restField}
                                  label="Hình ảnh"
                                  name={[name, 'image']}
                                >
                                  <ImagePicker />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={14}>
                                <Form.Item
                                  {...restField}
                                  label="Mô tả hình ảnh"
                                  name={[name, 'description']}
                                >
                                  <Input placeholder="Ví dụ: Giao diện đặt hàng trên điện thoại" />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={2}>
                                <Button danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                              </Col>
                            </Row>
                          ))}

                          <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ image: null, description: '' })}>
                            Thêm hình ảnh
                          </Button>
                        </Space>
                      )}
                    </Form.List>

                    <Space style={{ marginTop: 4 }}>
                      <Button onClick={generateLandingHTMLByAI} icon={<RobotOutlined />}>
                        Tạo HTML bằng AI
                      </Button>
                    </Space>
                  </>
                ),
              },
            ]}
          />
        </Card>

      </Form>

      <Affix offsetBottom={16}>
        <Card bodyStyle={{ padding: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Space wrap>
              <Button onClick={() => navigate('/seo-center/landing')}>Quay lại</Button>
              <Button onClick={handleViewLanding}>Xem bài viết</Button>
              <Button type="default" onClick={() => saveLanding('draft')}>Lưu nháp</Button>
              <Button type="primary" onClick={() => saveLanding('published')}>Lưu và publish</Button>
            </Space>
          </div>
        </Card>
      </Affix>
    </Space>
  );
};

export default LandingEditor;
